import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogActions, Typography, Button, Box, Link, Snackbar,
   Alert, Divider, IconButton, TextField, InputAdornment } from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import UserDetailsDialog from './UserDetailsDialog';
import { Organizationapplied, OrganizationHiretechishired, OrganizationHiretech } from "../../../helper/base_url";

const AppliedUsersDialog = ({
  open,
  onClose,
  selectedPost,
  refreshData,
}) => {
  const [appliedUsers, setAppliedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUserId, setLoadingUserId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);
  const [userDetailsId, setUserDetailsId] = useState(null);

  const [minScore, setMinScore] = useState('');
  const [maxScore, setMaxScore] = useState('');

  useEffect(() => {
    const fetchAppliedUsers = async () => {
      if (!selectedPost?._id) return;

      setLoading(true);
      try {
        const token = localStorage.getItem('company_token');
        const response = await axios.get(`${OrganizationHiretech}/${selectedPost._id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const users = response.data?.data?.appliedUsers || [];
        setAppliedUsers(users);
      } catch (error) {
        //console.error('Failed to fetch applied users:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load applied users',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAppliedUsers();
  }, [selectedPost]);

  const handleStatusUpdate = async (userId, status) => {
    try {
      setLoadingUserId(userId);
      const token = localStorage.getItem('company_token');
      await axios.put(
        `${Organizationapplied}`,
        { hiringId: selectedPost._id, userId, status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSnackbar({ open: true, message: 'Status updated successfully!', severity: 'success' });
      if (refreshData) refreshData();
    } catch (error) {
      //console.error('Error updating status:', error);
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    } finally {
      setLoadingUserId(null);
    }
  };

  const handleMarkAsHired = async (userId, ishired) => {
    try {
      setLoadingUserId(userId);
      const token = localStorage.getItem('company_token');
      await axios.put(
        `${OrganizationHiretechishired}`,
        { hiringId: selectedPost._id, userId, ishired },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSnackbar({
        open: true,
        message: ishired ? 'User marked as hired!' : 'User marked as not hired!',
        severity: 'success',
      });
      if (refreshData) refreshData();
    } catch (error) {
      //console.error('Error updating hired status:', error);
      setSnackbar({ open: true, message: 'Failed to update hired status', severity: 'error' });
    } finally {
      setLoadingUserId(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const openUserDetails = (userId) => {
    setUserDetailsId(userId);
    setUserDetailsOpen(true);
  };

  const filteredUsers = appliedUsers.filter(user => {
    const hoe = user?.highestHoeScore?.hoeScore || 0;
    const feedback = user?.highestHoeScore?.feedbackPoints || 0;
    const total = hoe + feedback;

    const min = minScore === '' ? -Infinity : parseInt(minScore, 10);
    const max = maxScore === '' ? Infinity : parseInt(maxScore, 10);

    return total >= min && total <= max;
  });

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{ position: 'absolute', right : 8, top : 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>

        {selectedPost && (
          <Box sx={{ px: 4, pt: 2, pb: 1, borderBottom : '1px solid #e0e0e0' }}>
            <Typography variant="h6" fontWeight={400} color="#043885" mb={1}>
              {selectedPost?.opportunityOverview?.title || 'N/A'}
            </Typography>
            <Box display="flex" alignItems="center" color="text.secondary" fontWeight={500} fontSize="0.95rem">
              <WorkIcon fontSize="small" sx={{ mr: 0.8, color: '#1976d2' }} />
              <span>Employment Type:</span>
              <Typography component="span" ml={0.5} fontWeight={600} color="text.primary">
                {selectedPost?.opportunityOverview?.opportunityType || 'N/A'}
              </Typography>
            </Box>
          </Box>
        )}

        <DialogTitle sx={{ pt: 3, pb: 1.5, fontWeight: 700, fontSize: '1.3rem', color: '#1976d2' }}>
          Applied Users
        </DialogTitle>

        {/* Score Filter Inputs */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, px: 4 }}>
          <TextField
            label="Min Score"
            type="number"
            size="small"
            value={minScore}
            onChange={(e) => setMinScore(e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">Pts</InputAdornment> }}
            fullWidth
          />
          <TextField
            label="Max Score"
            type="number"
            size="small"
            value={maxScore}
            onChange={(e) => setMaxScore(e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">Pts</InputAdornment> }}
            fullWidth
          />
        </Box>

        <Box sx={{ maxHeight : '400px', overflowY: 'auto', px: 4, pb: 3 }}>
          {loading ? (
            <Typography variant="body2" textAlign="center" color="text.secondary" mt={4}>
              Loading applied users...
            </Typography>
          ) : filteredUsers.length === 0 ? (
            <Typography variant="body2" textAlign="center" color="text.secondary" mt={4}>
              No users found in this score range.
            </Typography>
          ) : (
            filteredUsers.map((user, index) => {
              const hoe = user?.highestHoeScore?.hoeScore || 0;
              const feedback = user?.highestHoeScore?.feedbackPoints || 0;
              const total = hoe + feedback;

              return (
                <Box
                  key={user._id || index}
                  sx={{
                    mb: 3,
                    p: 3,
                    borderRadius: 2,
                    boxShadow: '0 1px 6px rgb(0 0 0 / 0.1)',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <Typography fontWeight={700} fontSize="1.1rem" mb={0.5} color="#052659">
                    {user.name || 'Unnamed User'}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    <strong>Email:</strong> {user.email || 'No Email'}
                  </Typography>

                  {user.resume && (
                    <Typography variant="body2" mb={0.5}>
                      <strong>Resume:</strong>{' '}
                      <Link href={user.resume} target="_blank" rel="noopener" underline="hover" color="primary">
                        View Resume
                      </Link>
                    </Typography>
                  )}

                  <Typography variant="body2" mb={0.5}>
                    <strong>Status:</strong>{' '}
                    <Box component="span" sx={{
                      fontWeight: 600,
                      color:
                        user.statusofStudent === 'Accepted'
                          ? 'green'
                          : user.statusofStudent === 'Rejected' || user.statusofStudent === 'Pending'
                          ? 'red'
                          : 'text.primary',
                    }}>
                      {user.statusofStudent || 'Not updated'}
                    </Box>
                  </Typography>

                  <Typography variant="body2" mb={1}>
                    <strong>Application Status:</strong>{' '}
                    <Box component="span" fontWeight={600} color={user.isHired ? 'green' : 'text.secondary'}>
                      {user.isHired ? 'Hired' : 'Not Hired'}
                    </Box>
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <strong>Applied Date:</strong>{' '}
                    {user.appliedDate ? new Date(user.appliedDate).toLocaleString() : 'Not Available'}
                  </Typography>

                  {user.highestHoeScore && (
                    <>
                      {/* <Typography variant="body2" color="text.secondary" mb={1}>
                        <strong>Highest HOE Score:</strong> {hoe}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <strong>Feedback Points:</strong> {feedback}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <strong>Total Points:</strong> {total}
                      </Typography> */}
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        <strong>Highest HOE Score:</strong> {total}
                      </Typography>
                    </>
                  )}

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1,
                      alignItems: 'center',
                    }}
                  >
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => openUserDetails(user._id)}
                      disabled={loadingUserId === user._id}
                      sx={{ textTransform: 'none' }}
                    >
                      View Details
                    </Button>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', minWidth : 200 }}>
                      {['Accepted', 'Rejected'].map((status) => (
                        <Button
                          key={status}
                          size="small"
                          variant={status === user.statusofStudent ? 'contained' : 'outlined'}
                          color={status === 'Accepted' ? 'success' : 'error'}
                          onClick={() => handleStatusUpdate(user._id, status)}
                          disabled={loadingUserId === user._id || status === user.statusofStudent}
                          sx={{ textTransform: 'none' }}
                        >
                          {status}
                        </Button>
                      ))}

                      {user.statusofStudent === 'Accepted' && (
                        <Button
                          size="small"
                          variant="contained"
                          color={user.isHired ? 'error' : 'primary'}
                          onClick={() => handleMarkAsHired(user._id, !user.isHired)}
                          disabled={loadingUserId === user._id}
                          sx={{ textTransform: 'none' }}
                        >
                          {user.isHired ? 'Mark as Not Hired' : 'Mark as Hired'}
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>

        <Divider sx={{ mt: 1, mb: 0 }} />

        <DialogActions sx={{ px: 4, py: 2 }}>
          <Button onClick={onClose} variant="outlined" color="primary" sx={{ fontWeight: 600 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width : '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <UserDetailsDialog
        open={userDetailsOpen}
        onClose={() => setUserDetailsOpen(false)}
        userId={userDetailsId}
      />
    </>
  );
};

export default AppliedUsersDialog;
